import { Box, BoxProps } from "@mui/material"
import { SxProps } from "@mui/system"
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation"
import React, { MutableRefObject, ReactNode } from "react"
import { useTransitions } from "src/hooks/use-transitions"

import { instantScroll } from "../../utilities/common"
import { s } from "../../utilities/common"

export interface Colors {
  focused: string
  blurred: string
}
export interface GenericButtonProps
  extends Omit<BoxProps, "onFocus" | "onBlur"> {
  sx?: SxProps
  children: ReactNode
  focusKey?: string
  onEnterPress: () => void
  backgroundColors?: Colors
  onFocus?: (param?: FocusEvent) => void
  onBlur?: (param?: FocusEvent) => void
  prefScroll?: boolean
  scroll?: {
    scrollRef: MutableRefObject<HTMLElement | null>
    offsetTop?: number
    absoluteTop?: number
  }
  shadow?: boolean
  ariaLabel?: string
}

export const GenericButton = ({
  sx,
  children,
  focusKey,
  onEnterPress,
  backgroundColors,
  onFocus,
  onBlur,
  prefScroll = false,
  scroll,
  shadow = false,
  ariaLabel,
  ...rest
}: GenericButtonProps) => {
  const transition = useTransitions()
  const { focusSelf, focused, ref } = useFocusable({
    focusKey,
    onEnterPress: onEnterPress,
    onFocus: ({ top: y }, __, query) => {
      if (
        !(query && query.noScroll) &&
        prefScroll &&
        scroll?.scrollRef.current
      ) {
        instantScroll(scroll.scrollRef, query, transition)
        const value = Math.min(
          0,
          scroll.absoluteTop !== undefined
            ? scroll.absoluteTop
            : -y + (scroll.offsetTop ?? 0),
        )
        scroll.scrollRef.current.style.transform =
          "translate3d(0, " + Math.min(0, value) + "px, 0)"
      }
      if (onFocus) onFocus()
    },
    onBlur: () => {
      if (onBlur) onBlur()
    },
  })

  return (
    <Box
      {...rest}
      aria-label={ariaLabel}
      tabIndex={0}
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={() => onEnterPress()}
      ref={ref}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        "& > :not(:last-child)": {
          mr: s("10px"),
        },
        width: "auto",
        height: s("72px"),
        pl: s("30px"),
        pr: s("30px"),
        borderRadius: s("4px"),
        transform: focused ? "scale(1.22)" : undefined,
        zIndex: focused ? 2 : 1,
        boxShadow: shadow
          ? focused
            ? `${s("0px 30px 112px 0px")} rgba(0, 0, 0, 0.60)`
            : "0px 8px 16px 0px rgba(0, 0, 0, 0.40)"
          : undefined,
        background: focused
          ? (backgroundColors?.focused ?? "rgba(255,255,255,1)")
          : (backgroundColors?.blurred ?? "rgba(255,255,255,0.5)"),
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}
